import { TextField } from '@mui/material';
import { updateDosageThunk, getDosageByIdThunk, getDosagesThunk } from '@redux/features/dosage.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import React, { FC, useEffect } from 'react';
import * as Yup from 'yup';

type IProps = {
    show: boolean;
    handleClose: () => void;
    dosageId: number;
};

const UpdateDosageModal: FC<IProps> = ({ show, handleClose, dosageId }) => {
    const { loading, dosage } = useAppSelector((state: RootState) => state.dosageState);
    // ----------------------------------------------------------------
    const dispatch = useAppDispatch();
    // --------------------------------------------------
    const validationSchema = Yup.object({
        dosageName: Yup.string().required('Dosage Name Is Required'),
    });
    const handleSubmit = (values: any) => {
        dispatch(updateDosageThunk({ id: dosageId, dosageName: values.dosageName })).then((res: any) => {
            if (res.meta.requestStatus === 'fulfilled') {
                dispatch(getDosagesThunk());
                handleClose();
                formik.resetForm();
            }
        });
    };
    // --------------------------------------------------
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            dosageName: dosage?.dosageName || ``,
        },
        validationSchema,
        onSubmit: handleSubmit,
    });
    // --------------------------------------------------
    useEffect(() => {
        if (show && dosageId) {
            dispatch(getDosageByIdThunk(dosageId));
        }
    }, [show, dosageId, dispatch]);

    useEffect(() => {
        if (dosage && show) {
            formik.setValues({
                dosageName: dosage.dosageName || ``,
            });
        }
    }, [dosage, show]);

    return (
        <Modal
            show={show}
            onHide={() => handleClose()}
            dialogClassName="w-full bg-white p-3 shadow rounded-md overflow-scroll md:w-[500px] md:max-h-[80vh]"
        >
            <Modal.Header>
                <Modal.Title className="text-lg font-semibold">Update Dosage</Modal.Title>
            </Modal.Header>
            <Modal.Body className="my-5">
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
                    <TextField
                        disabled={loading}
                        fullWidth
                        size="small"
                        id="dosageName"
                        name="dosageName"
                        label="Dosage Name"
                        value={formik.values.dosageName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.dosageName && Boolean(formik.errors.dosageName)}
                        helperText={formik.touched.dosageName ? String(formik.errors.dosageName || '') : ''}
                    />
                </form>
            </Modal.Body>
            <Modal.Footer className="flex items-center justify-center gap-2 mt-5">
                <ButtonBase
                    innerText="Cancel"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={() => {
                        handleClose();
                        formik.resetForm();
                    }}
                    disabled={loading}
                />
                <ButtonBase
                    innerText="Update Dosage"
                    className="bg-yellow-600 hover:bg-yellow-800 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={formik.handleSubmit}
                    disabled={loading || !formik.values.dosageName}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default UpdateDosageModal;
